import { NavLink as Link } from "react-router-dom";
import styled from "styled-components";
import { colors, easing, fonts, layout, media } from "../../../theme";

export const Nav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  height: ${({ $compact }) =>
    $compact ? layout.navHeightCompact : layout.navHeight};
  background-color: ${({ $solid }) =>
    $solid ? "rgba(28, 28, 28, 0.9)" : "rgba(28, 28, 28, 0)"};
  backdrop-filter: ${({ $solid }) =>
    $solid ? "blur(14px) saturate(140%)" : "none"};
  -webkit-backdrop-filter: ${({ $solid }) =>
    $solid ? "blur(14px) saturate(140%)" : "none"};
  box-shadow: ${({ $solid }) =>
    $solid ? "0 10px 30px rgba(0, 0, 0, 0.18)" : "none"};
  border-bottom: 1px solid
    ${({ $solid }) => ($solid ? "rgba(255, 255, 255, 0.06)" : "transparent")};
  transition: height 0.4s ${easing.out}, background-color 0.4s ease,
    box-shadow 0.4s ease, border-color 0.4s ease;

  ${media.mobile} {
    height: ${layout.navHeightCompact};
  }
`;

export const NavInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  height: 100%;
  max-width: ${layout.maxWidth};
  margin: 0 auto;
  padding: 0 ${layout.gutter};
`;

export const NavLogo = styled(Link)`
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0.55rem 0;
  transition: transform 0.4s ${easing.out};

  &:hover {
    transform: scale(1.04);
  }
`;

export const NavLogoImg = styled.img`
  width: auto;
  height: 100%;
  object-fit: contain;
`;

export const NavMenu = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  white-space: nowrap;

  ${media.mobile} {
    display: none;
  }
`;

export const NavLink = styled(Link)`
  position: relative;
  padding: 0.6rem 1rem;
  color: rgba(255, 255, 234, 0.85);
  font-size: 1rem;
  font-weight: 500;
  text-decoration: none;
  transition: color 0.3s ease;

  &::after {
    content: "";
    position: absolute;
    left: 1rem;
    right: 1rem;
    bottom: 0.3rem;
    height: 2px;
    border-radius: 2px;
    background-color: ${colors.primary};
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.4s ${easing.out};
  }

  &:hover {
    color: ${colors.backgroundMain};
  }

  &:hover::after,
  &.active::after {
    transform: scaleX(1);
  }

  &.active {
    color: ${colors.primary};
    font-weight: 600;
  }
`;

export const NavRight = styled.div`
  display: flex;
  align-items: center;

  ${media.mobile} {
    display: none;
  }
`;

export const MenuToggle = styled.button`
  display: none;
  position: relative;
  width: 2.75rem;
  height: 2.75rem;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;

  span {
    position: absolute;
    left: 0.6rem;
    right: 0.6rem;
    height: 2px;
    border-radius: 2px;
    background-color: ${colors.backgroundMain};
    transition: transform 0.4s ${easing.out}, opacity 0.3s ease;
  }

  span:nth-child(1) {
    top: calc(50% - 7px);
    transform: ${({ $open }) =>
      $open ? "translateY(6px) rotate(45deg)" : "none"};
  }

  span:nth-child(2) {
    top: calc(50% - 1px);
    opacity: ${({ $open }) => ($open ? 0 : 1)};
  }

  span:nth-child(3) {
    top: calc(50% + 5px);
    transform: ${({ $open }) =>
      $open ? "translateY(-6px) rotate(-45deg)" : "none"};
  }

  ${media.mobile} {
    display: block;
  }
`;

export const MobileMenu = styled.div`
  display: none;

  ${media.mobile} {
    position: fixed;
    inset: 0;
    z-index: 99;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: calc(${layout.navHeightCompact} + 2.5rem) ${layout.gutter} 2rem;
    background: radial-gradient(
        ellipse at 100% 100%,
        rgba(255, 195, 11, 0.18),
        transparent 60%
      ),
      ${colors.secondary};
    opacity: ${({ $open }) => ($open ? 1 : 0)};
    visibility: ${({ $open }) => ($open ? "visible" : "hidden")};
    transition: opacity 0.4s ease, visibility 0.4s;
  }
`;

const staggeredItem = ({ $open, $index }) => `
  opacity: ${$open ? 1 : 0};
  transform: ${$open ? "none" : "translateY(1rem)"};
  transition: opacity 0.5s ease, transform 0.6s ${easing.out}, color 0.3s ease;
  transition-delay: ${$open ? 0.1 + $index * 0.06 : 0}s;
`;

export const MobileLink = styled(Link)`
  padding: 0.5rem 0;
  font-family: ${fonts.heading};
  font-size: 2.25rem;
  font-weight: 500;
  color: ${colors.backgroundMain};
  text-decoration: none;
  ${staggeredItem}

  &.active {
    color: ${colors.primary};
  }
`;

export const MobileMenuFooter = styled.div`
  margin-top: auto;
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 234, 0.12);
  ${staggeredItem}
`;
